/**
 * Structured spec + benchmark data for each CPU SKU we sell. Rendered as a
 * one-line summary under the CPU title in the configurator picker so the
 * customer doesn't have to decode the description blob.
 *
 * PassMark scores are per-CPU (single-socket). For dual-socket builds, the
 * multi-thread total roughly doubles (with ~5-10% scaling loss).
 *
 * Sources: Intel ARK + PassMark CPU Benchmark database, accessed 2026-05.
 */
export type CpuSpec = {
  cores: number
  threads: number
  baseGhz: number
  turboGhz: number
  l3CacheMb: number
  tdpWatts: number
  passmarkMulti: number  // per-CPU multi-thread score (PassMark CPU Mark)
  passmarkSingle: number // single-thread score
  /** Optional one-liner highlight rendered in bold under the spec line —
   *  reserved for the standout reason to pick this SKU over the others. */
  note?: string
}

export const CPU_SPECS: Record<string, CpuSpec> = {
  // Intel ARK SR2N7 — Broadwell-EP, 14 cores. The all-rounder default.
  "xeon-e5-2680-v4": {
    cores: 14, threads: 28,
    baseGhz: 2.4, turboGhz: 3.3,
    l3CacheMb: 35, tdpWatts: 120,
    passmarkMulti: 18500, passmarkSingle: 1700,
  },
  // Intel ARK SR2P2 — Broadwell-EP, 14 cores at low-power 65W TDP. Best for
  // 1U / quiet / efficiency-sensitive builds.
  "xeon-e5-2650l-v4": {
    cores: 14, threads: 28,
    baseGhz: 1.7, turboGhz: 2.5,
    l3CacheMb: 35, tdpWatts: 65,
    passmarkMulti: 14000, passmarkSingle: 1350,
    note: "Lowest TDP — 65 W per socket, best for quiet 1U builds",
  },
  // Intel ARK SR2P5 — Broadwell-EP, 8 high-frequency cores. Best per-core
  // performance for licensing-sensitive workloads (Windows Server per-core,
  // Oracle DB) or single-thread-bound apps.
  "xeon-e5-2667-v4": {
    cores: 8, threads: 16,
    baseGhz: 3.2, turboGhz: 3.6,
    l3CacheMb: 25, tdpWatts: 135,
    passmarkMulti: 13500, passmarkSingle: 1900,
    note: "Highest single-thread performance",
  },
  // Intel ARK SR2JT — Broadwell-EP, 16 cores at the same 120W TDP as the 2680.
  // Cheap density pick for VMware / container hosts.
  "xeon-e5-2683-v4": {
    cores: 16, threads: 32,
    baseGhz: 2.1, turboGhz: 3.0,
    l3CacheMb: 40, tdpWatts: 120,
    passmarkMulti: 20000, passmarkSingle: 1500,
    note: "Most cores at low TDP — VMware / container density",
  },
  // Intel ARK SR2N2 — Broadwell-EP, 14 cores at higher base/turbo than the
  // 2680. Drop-in upgrade.
  "xeon-e5-2690-v4": {
    cores: 14, threads: 28,
    baseGhz: 2.6, turboGhz: 3.5,
    l3CacheMb: 35, tdpWatts: 135,
    passmarkMulti: 19500, passmarkSingle: 1850,
    note: "Faster all-rounder — direct 2680 upgrade",
  },
  // Intel ARK SR2JV — Broadwell-EP, 18 cores at the chassis TDP cap.
  // Multi-thread density sweet spot.
  "xeon-e5-2697-v4": {
    cores: 18, threads: 36,
    baseGhz: 2.3, turboGhz: 3.6,
    l3CacheMb: 45, tdpWatts: 145,
    passmarkMulti: 22000, passmarkSingle: 1750,
    note: "Density sweet spot — 36 threads per socket",
  },
  // Intel ARK SR2JS — Broadwell-EP, the maximum cores compatible with this
  // chassis. Hits the 145W TDP cap.
  "xeon-e5-2699-v4": {
    cores: 22, threads: 44,
    baseGhz: 2.2, turboGhz: 3.6,
    l3CacheMb: 55, tdpWatts: 145,
    passmarkMulti: 25000, passmarkSingle: 1700,
    note: "Maximum compatible cores — 88 threads on dual-socket",
  },
}

/** Format a CPU spec as one short line for the picker. */
export function formatCpuSpecLine(spec: CpuSpec): string {
  return (
    `${spec.cores}C/${spec.threads}T · ` +
    `${spec.baseGhz.toFixed(1)}–${spec.turboGhz.toFixed(1)} GHz · ` +
    `${spec.l3CacheMb} MB L3 · ` +
    `${spec.tdpWatts}W TDP · ` +
    `PassMark ${spec.passmarkMulti.toLocaleString()} multi / ${spec.passmarkSingle.toLocaleString()} single`
  )
}
